// A scene's spoken lines with "Move to…" and "Split here" (Keaton 2026-10-31: move a character's line into its own
// scene, or split the narration from the character lines so lip sync starts at the right moment). Free; recorded takes
// go with their lines (api v4/scenelines.ts); the Director hears about every tap and it can be undone.
import { useState } from "react";
import * as act from "../actions";
import { api } from "../api";
import { director } from "../director";
import { bump, toast } from "../store";
import type { Project } from "../types";
import { Icon } from "./bits";

type Line = { speaker: string; text: string };

export function SceneLines({ p, index, name }: { p: Project; index: number; name: (id: string) => string }) {
  const scene = p.script?.scenes[index] as { voiceover: string; lines?: Line[] } | undefined;
  const [moving, setMoving] = useState<number | "narrator" | null>(null);
  const [busy, setBusy] = useState(false);
  if (!scene) return null;
  const n = index + 1;
  const total = p.script?.scenes.length ?? 0;
  const lines = scene.lines ?? [];
  const run = async (body: Parameters<typeof act.editLines>[1], said: string) => {
    setBusy(true);
    await act.editLines(p.id, body, said);
    setBusy(false);
    setMoving(null);
  };
  const moveTo = (line: number | "narrator", v: string) => {
    if (!v) return;
    const what = line === "narrator" ? "the narrator's line" : `line ${line}`;
    if (v === "new") void run({ op: "move", scene: n, line, newAfter: n }, `moved scene ${n}'s ${what} into a new scene ${n + 1}`);
    else void run({ op: "move", scene: n, line, toScene: Number(v) }, `moved scene ${n}'s ${what} to scene ${v}`);
  };
  const picker = (line: number | "narrator") =>
    moving === line && (
      <select className="scene-line-move" autoFocus disabled={busy} defaultValue="" onChange={(e) => moveTo(line, e.target.value)} onBlur={() => setMoving(null)} aria-label="Move the line to">
        <option value="" disabled>
          Move to…
        </option>
        <option value="new">A new scene after this one</option>
        {Array.from({ length: total }, (_, i) => i + 1)
          .filter((k) => k !== n)
          .map((k) => (
            <option key={k} value={k}>
              Scene {k}
            </option>
          ))}
      </select>
    );
  const moveBtn = (line: number | "narrator") => (
    <button type="button" data-action="scene.line-move" className="icon-btn" disabled={busy} title="Move to…" aria-label={`Move ${line === "narrator" ? "the narrator's line" : `line ${line}`} to another scene`} onClick={() => setMoving(line)}>
      <Icon name="chevron" size={14} />
    </button>
  );
  // 2026-11-03 (Keaton's report: a fast announcer read before the first render): a line's speed, for every render
  const sceneId = p.script?.scenes[index]?.id;
  const speeds = (sceneId && p.shots[sceneId]?.lineSpeeds) || {};
  const speedPick = (key: string, what: string) => {
    const v = speeds[key] ?? 1;
    const opts = [...new Set([0.75, 0.9, 1, 1.1, 1.25, 1.5, 1.75, 2, v])].sort((a, b) => a - b);
    return (
      <select
        data-action="scene.line-speed"
        className={`scene-line-speed${v !== 1 ? " is-on" : ""}`}
        value={String(v)}
        disabled={busy || !sceneId}
        aria-label={`Speed of ${what}`}
        title="Speed (the pitch is kept)"
        onChange={async (e) => {
          const speed = Number(e.target.value);
          setBusy(true);
          try {
            await api.setLineSpeed(p.id, sceneId!, key, speed);
            bump("project");
            director.context(`The user set scene ${n}'s ${what} to ${speed}x speed by tapping (free; from the next render).`);
          } catch (err) {
            toast(`Couldn't change the speed. ${(err as Error).message}`, "bad");
          } finally {
            setBusy(false);
          }
        }}
      >
        {opts.map((o) => (
          <option key={o} value={String(o)}>
            {o === 1 ? "1×" : `${o}×`}
          </option>
        ))}
      </select>
    );
  };
  return (
    <>
      {scene.voiceover && (
        <p className="detail-vo">
          “{scene.voiceover}” {speedPick("vo", "narration")} {(lines.length > 0 || total > 1) && moveBtn("narrator")}
          {picker("narrator")}
        </p>
      )}
      {!!lines.length && (
        <ul className="detail-lines" aria-label="Dialogue">
          {lines.map((l, i) => (
            <li key={i}>
              <b>{name(l.speaker)}</b> {l.text}{" "}
              <span className="scene-line-acts">
                {speedPick(`d${i}`, `line ${i + 1}`)}
                {moveBtn(i + 1)}
                {(i > 0 || !!scene.voiceover.trim()) && (
                  <button type="button" data-action="scene.line-split" className="icon-btn" disabled={busy} title="Split here" aria-label={`Split scene ${n} before this line`} onClick={() => void run({ op: "split", scene: n, beforeLine: i + 1 }, `split scene ${n} before line ${i + 1}`)}>
                    <Icon name="stack" size={14} />
                  </button>
                )}
              </span>
              {picker(i + 1)}
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
